// Session-cookie auth state (`sneethel.sid`) for the account-holder login
// path only — the guest OTP path never touches this context at all (no
// session exists for a guest, by design; see backend/src/routes/guestRoutes.js).
// Same shape as admin/src/context/AuthContext.jsx — see that file's header
// comment for the full reasoning. Kept as a portal-local copy, not moved to
// shared/ (out of scope for this task).
//
// Portal-specific addition over admin's copy: this app only has ONE
// legitimate role, 'customer'. A successful login as any other role
// (sales/admin — the only accounts the seed script actually creates today)
// is not a security bypass (every real API this app calls still enforces
// its own requireRole('customer') server-side) but IS the wrong app for that
// person, so it must never be treated as "logged in" here — see
// pages/LoginPage.jsx's handling of `user.role !== 'customer'`, which
// proactively calls `logout()` rather than leaving a live non-buyer session
// sitting in a portal browser tab.

import { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient, { ApiError } from 'shared/api/client.js';

const AuthContext = createContext(null);

async function fetchMe() {
  try {
    const data = await apiClient.get('/auth/me');
    return data.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const loginMutation = useMutation({
    mutationFn: ({ email, password }) => apiClient.post('/auth/login', { email, password }),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data.user);
    },
  });

  // POST /auth/register — customer self-registration. Auto-logs in on
  // success (routes/authRoutes.js's own register handler calls
  // establishSession, the identical path POST /login's success case uses),
  // so this mutation's onSuccess mirrors loginMutation's exactly.
  const registerMutation = useMutation({
    mutationFn: (values) => apiClient.post('/auth/register', values),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data.user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => apiClient.post('/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
    },
  });

  const value = useMemo(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isError: meQuery.isError,
      login: loginMutation.mutateAsync,
      isLoggingIn: loginMutation.isPending,
      loginError: loginMutation.error,
      resetLoginError: loginMutation.reset,
      register: registerMutation.mutateAsync,
      isRegistering: registerMutation.isPending,
      registerError: registerMutation.error,
      logout: logoutMutation.mutateAsync,
    }),
    [
      meQuery.data,
      meQuery.isLoading,
      meQuery.isError,
      loginMutation,
      registerMutation,
      logoutMutation,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>.');
  }
  return ctx;
}
